import { useState } from "react";
import { useDemoState } from "@/data/demoState";
import { ChevronLeft, ChevronRight, RotateCcw, Layers, X, Info, ChevronDown, Building2, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

export default function ScenarioHub() {
  const { activeScenario, activeWorkspace, workspace, switchScenario, switchWorkspace, resetState, nextScenario, prevScenario, actionLog, scenarios, workspaces } = useDemoState();
  const [open, setOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [tab, setTab] = useState('scenarios');
  const current = scenarios.find(s => s.id === activeScenario);
  const idx = scenarios.findIndex(s => s.id === activeScenario);

  const handleSwitchWorkspace = (wsId) => {
    switchWorkspace(wsId);
    const ws = workspaces.find(w => w.id === wsId);
    toast.success(`Switched to ${ws?.name}`, { description: ws?.tagline });
  };

  return (
    <>
      <div className="fixed top-0 right-0 z-[9998] flex items-center gap-1 pr-4 pt-2" data-testid="scenario-hub-trigger">
        <button onClick={() => setOpen(!open)}
          className="flex items-center gap-2 px-3 py-1.5 text-white rounded-lg text-[11px] font-medium shadow-lg hover:opacity-90 transition-all"
          style={{ backgroundColor: workspace.accentColor }}>
          <Layers className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{workspace.name}</span>
          <span className="sm:hidden">Demo</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <div className="fixed top-10 right-4 z-[9999] w-[400px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden scenario-hub-enter" data-testid="scenario-hub-panel">
          {/* Header */}
          <div className="px-4 py-3 text-white flex items-center justify-between" style={{ backgroundColor: workspace.accentColor }}>
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4" />
              <span className="text-sm font-semibold">Demo Control Centre</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
          </div>

          {/* Tab switcher */}
          <div className="flex border-b border-slate-100">
            <button onClick={() => setTab('scenarios')} data-testid="hub-tab-scenarios"
              className={`flex-1 text-xs font-medium py-2.5 text-center transition-colors ${tab === 'scenarios' ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-400 hover:text-slate-600'}`}>
              Scenarios
            </button>
            <button onClick={() => setTab('workspaces')} data-testid="hub-tab-workspaces"
              className={`flex-1 text-xs font-medium py-2.5 text-center transition-colors ${tab === 'workspaces' ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-400 hover:text-slate-600'}`}>
              Workspaces
            </button>
          </div>

          {tab === 'workspaces' && (
            <div className="max-h-[400px] overflow-y-auto">
              {/* Active workspace */}
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Active workspace</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ backgroundColor: workspace.accentColor }}>
                    {workspace.logoInitials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{workspace.name}</p>
                    <p className="text-[11px] text-slate-500">{workspace.tagline}</p>
                  </div>
                </div>
              </div>

              {/* Workspace list */}
              {workspaces.map(ws => (
                <button key={ws.id} onClick={() => handleSwitchWorkspace(ws.id)}
                  data-testid={`workspace-${ws.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${ws.id === activeWorkspace ? 'bg-slate-50' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: ws.accentColor }}>
                      {ws.logoInitials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-xs font-semibold ${ws.id === activeWorkspace ? 'text-slate-900' : 'text-slate-700'}`}>{ws.name}</p>
                        {ws.id === activeWorkspace && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                      </div>
                      <p className="text-[10px] text-slate-400">{ws.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-1.5 pl-12">
                    <Badge className="text-[9px] bg-slate-100 text-slate-500">{ws.type}</Badge>
                    <Badge className="text-[9px] bg-slate-100 text-slate-500">{ws.operatingModel}</Badge>
                  </div>
                </button>
              ))}
            </div>
          )}

          {tab === 'scenarios' && (
            <>
              {/* Current scenario */}
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Active scenario</span>
                  <Badge className="text-[9px] bg-blue-100 text-blue-700">{idx + 1} of {scenarios.length}</Badge>
                </div>
                <p className="text-sm font-semibold text-slate-900">{current?.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{current?.desc}</p>
              </div>

              {/* Controls */}
              <div className="px-4 py-2 flex items-center gap-2 border-b border-slate-100">
                <button onClick={prevScenario} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors" data-testid="scenario-prev">
                  <ChevronLeft className="w-3.5 h-3.5" />Previous
                </button>
                <button onClick={nextScenario} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors" data-testid="scenario-next">
                  Next<ChevronRight className="w-3.5 h-3.5" />
                </button>
                <button onClick={resetState} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors ml-auto" data-testid="scenario-reset">
                  <RotateCcw className="w-3.5 h-3.5" />Reset
                </button>
              </div>

              {/* Demo notes */}
              <div className="px-4 py-2 border-b border-slate-100">
                <button onClick={() => setNotesOpen(!notesOpen)} className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700">
                  <Info className="w-3 h-3" />{notesOpen ? 'Hide' : 'Show'} demo notes
                </button>
                {notesOpen && current?.notes && (
                  <p className="text-xs text-slate-600 mt-2 bg-blue-50 rounded-lg p-2.5 leading-relaxed">{current.notes}</p>
                )}
              </div>

              {/* Scenario list */}
              <div className="max-h-[250px] overflow-y-auto">
                {scenarios.map((s, i) => (
                  <button key={s.id} onClick={() => switchScenario(s.id)}
                    data-testid={`scenario-${s.id}`}
                    className={`w-full text-left px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 transition-colors ${s.id === activeScenario ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-slate-400 w-4">{i + 1}</span>
                      <div className="min-w-0">
                        <p className={`text-xs font-medium truncate ${s.id === activeScenario ? 'text-blue-700' : 'text-slate-700'}`}>{s.name}</p>
                        <p className="text-[10px] text-slate-400 truncate">{s.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Action log */}
          {actionLog.length > 0 && (
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-200">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Recent actions</p>
              <div className="space-y-1 max-h-[80px] overflow-y-auto">
                {actionLog.slice(0, 5).map(a => (
                  <div key={a.id} className="flex items-center gap-2 text-[10px]">
                    <span className="text-slate-400">{a.time}</span>
                    <span className="text-slate-600 truncate">{a.action}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
